import React, { useEffect } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { BorrowerRole } from 'enums/BorrowerRole';
import {
  getEmploymentVariableName,
  getIsInitialValueNotNull,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import {
  CurrentEmploymentVariable,
  EmploymentStatusInformationInputLabel,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.enums';
import NumberInput from 'components/Inputs/NumberInput';
import Select from 'components/Select';
import {
  BRANCH_OF_SERVICE_OPTIONS,
  MILITARY_PAYGRADE_OPTIONS,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.constants';
import { getErrorMessageForRequiredFields } from 'prequalificationV2/utils/stringUtils';
import { isValidDateBeforeNow } from 'prequalificationV2/utils/dateUtils';
import { DateFormat } from 'enums/DateFormat';
import { getMessageForInvalidFields } from 'prequalificationV2/utils/validationUtils';
import styles from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/InformationTabs.module.scss';

const RetiredMilitaryInformation = ({
  formMethods,
  borrowerType,
  watcher,
}: {
  formMethods: UseFormReturn<any, any>;
  borrowerType: BorrowerRole;
  watcher: any;
}) => {
  const { variables } = useAppSelector((state) => state.application);
  const applicationVariables = variables!;

  const {
    register,
    clearErrors,
    formState: { errors },
    trigger,
    setValue,
  } = formMethods;

  useEffect(() => {
    register(getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)],
        message: getErrorMessageForRequiredFields(EmploymentStatusInformationInputLabel.RetiredBranchOfService),
      },
    });
    register(getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)],
        message: getErrorMessageForRequiredFields(EmploymentStatusInformationInputLabel.RetiredMilitaryPaygrade),
      },
    });
    register(getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType), {
      required: {
        value: !watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)],
        message: getErrorMessageForRequiredFields(EmploymentStatusInformationInputLabel.RetirementDate),
      },
      validate: (value) => {
        return (
          isValidDateBeforeNow(value, DateFormat.MonthYear) ||
          getMessageForInvalidFields(EmploymentStatusInformationInputLabel.RetirementDate)
        );
      },
    });
  }, []);

  return (
    <div className={styles.formRow}>
      <Select
        label={EmploymentStatusInformationInputLabel.RetiredBranchOfService}
        name={getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)}
        value={BRANCH_OF_SERVICE_OPTIONS.find(
          (option) =>
            option.value ===
            watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)],
        )}
        inputContainerClassName={styles.input}
        onChange={(value) => {
          clearErrors(getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType));
          setValue(getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType), value);
        }}
        options={BRANCH_OF_SERVICE_OPTIONS}
        isSearchable
        errorMessage={
          errors[getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)]?.message
        }
        isClearable
        isDisabled={getIsInitialValueNotNull(
          applicationVariables[
            getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)
          ],
        )}
      />
      <div className={styles.retiredMilitaryFormRow}>
        <Select
          label={EmploymentStatusInformationInputLabel.RetiredMilitaryPaygrade}
          name={getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)}
          value={MILITARY_PAYGRADE_OPTIONS.find(
            (option) =>
              option.value ===
              watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)],
          )}
          inputContainerClassName={styles.input}
          onChange={(value) => {
            clearErrors(getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType));
            setValue(getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType), value);
          }}
          options={MILITARY_PAYGRADE_OPTIONS}
          isSearchable
          errorMessage={
            errors[getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)]?.message
          }
          isClearable
          isDisabled={getIsInitialValueNotNull(
            applicationVariables[
              getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)
            ],
          )}
        />
        <NumberInput
          label={EmploymentStatusInformationInputLabel.RetirementDate}
          name={getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)}
          value={watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)]}
          errorMessage={
            errors[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)]?.message
          }
          onChange={(event) => {
            clearErrors(getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType));
            setValue(
              getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType),
              event.target.value,
            );
          }}
          className={styles.input}
          onBlur={() => {
            if (watcher[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)]) {
              trigger(getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType));
            }
          }}
          placeholder="MM/YYYY"
          format="##/####"
          mask="_"
          allowEmptyFormatting
          disabled={getIsInitialValueNotNull(
            applicationVariables[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)],
          )}
        />
      </div>
    </div>
  );
};

export default RetiredMilitaryInformation;
